import UserTypes from './user.types';
import UserState, { timeRange, Metric as StatusControl } from './user.state';
import { ThunkDispatch } from 'redux-thunk';
import RootState from '../state';
import * as AuthAPI from 'services/api/api.auth';
import * as ContactAPI from 'services/api/api.contact';
import * as AgentAPI from 'services/api/api.agent';
import AuthState from 'store/auth/auth.state';
import { AxiosError } from 'axios';
import { deleteSATabSession, getMidnight, getParameterByName } from 'utils';
import { setRedirect } from '../global/global.actions';
import { AppFeatures, IFeature } from "store/app/app.features";
import { MS_IN_HOUR } from 'constants/index';

export interface UserSetStateAction {
    state: connect.AgentStateDefinition;
}

export interface UserAuthenticatedAction {
    token: string;
    refreshToken: string;
    agentID: string;
    features: string[];
    roles: string[];
    hierarchyStructure?: string;
}

export interface ExternalUserAuthRequestAction {
    credentials: any,
    ccpURL: string
}

export type UserAction =
    | { type: typeof UserTypes.LOGOUT }
    | { type: typeof UserTypes.LOGIN; payload: { username?: string; password?: string } }
    | { type: typeof UserTypes.USER_SET_STATE; payload: UserSetStateAction }
    | { type: typeof UserTypes.TOKEN_ERROR }
    | { type: typeof UserTypes.USER_AVAILABLE }
    | { type: typeof UserTypes.USER_NOT_AVAILABLE }
    | { type: typeof UserTypes.GET_QUICK_CONNECTS; payload?: Array<any> }
    | { type: typeof UserTypes.SHOW_QUICK_CONNECTS }
    | { type: typeof UserTypes.HIDE_QUICK_CONNECTS }
    | { type: typeof UserTypes.AFTER_CALL_WORK }
    | { type: typeof UserTypes.AFTER_CALL_WORK_END }
    | { type: typeof UserTypes.CALL_ISSUE }
    | { type: typeof UserTypes.AUTHENTICATING_USER }
    | { type: typeof UserTypes.REFRESH }
    | { type: typeof UserTypes.USER_AUTH_ERROR; payload: { error: string } }
    | { type: typeof UserTypes.VALIDATING_TOKEN }
    | { type: typeof UserTypes.TOKEN_REQUESTED }
    | { type: typeof UserTypes.USER_AUTHENTICATED; payload: UserAuthenticatedAction }
    | { type: typeof UserTypes.USER_AUTHENTICATION_HAS_SET }
    | { type: typeof UserTypes.REQUESTING_TOKEN }
    | { type: typeof UserTypes.TOGGLE_QUICK_CONNECTS }
    | { type: typeof UserTypes.USER_ERROR }
    | { type: typeof UserTypes.USER_LOADED; payload: Partial<UserState> }
    | { type: typeof UserTypes.FETCH_AGENT_STATS; payload: Array<any> }
    | { type: typeof UserTypes.NEW_PASSWORD_REQUIRED }
    | {
        type: typeof UserTypes.NEW_PASSWORD_REQUIRED_CHANGE;
        payload: NewPasswordRequiredChangeAction;
    }
    | { type: typeof UserTypes.FORGOT_PASSWORD }
    | { type: typeof UserTypes.REMEMBERED_PASSWORD }
    | { type: typeof UserTypes.FORGOT_PASSWORD_CONFIRM; payload: ForgotPasswordConfirmAction }
    | { type: typeof UserTypes.FORGOT_PASSWORD_GET_CODE; payload: { username: string } }
    | { type: typeof UserTypes.INCORRECT_TOKEN }
    | { type: typeof UserTypes.MFA_REQUIRED, payload: NonNullable<AuthState['mfa']> }
    | { type: typeof UserTypes.SUBMIT_MFA, payload: string }
    | { type: typeof UserTypes.SET_AFTER_CALL_STATUS, payload: connect.AgentStateDefinition }
    | { type: typeof UserTypes.SET_TIME_RANGE, payload: timeRange }
    | { type: typeof UserTypes.AUTH_EXTERNAL_USER_REQUEST, payload: ExternalUserAuthRequestAction }
    | { type: typeof UserTypes.SET_LOGOUT_COUNTDOWN, payload: number }
    | { type: typeof UserTypes.SET_SHOW_NEXT_STATUS, payload: boolean }
    | { type: typeof UserTypes.SET_STATUS_CONTROL, payload: StatusControl }

export function userLoaded(user: Partial<UserState>): UserAction {
    return {
        type: UserTypes.USER_LOADED,
        payload: user,
    };
}

export function authError(error?: string) {
    return {
        type: UserTypes.USER_AUTH_ERROR,
        payload: {
            error: error || 'There was an error',
        },
    };
}

export function logout() {
    return (dispatch: ThunkDispatch<RootState, undefined, UserAction>, getState: () => RootState) => {
        deleteSATabSession()
        const companyID = getState().app.ID;
        const token = getState().auth.token;
        const user = getState().user;
        const instanceID = getState().app.instance!.ID;

        if (!token || !user) {
            return dispatch({
                type: UserTypes.LOGOUT,
            });
        }

        AuthAPI.logout(companyID, token, user.username, instanceID);

        return dispatch({
            type: UserTypes.LOGOUT,
        });
    };
}

interface NewPasswordRequiredChangeAction {
    username: string;
    newPassword: string;
}
export function changePassword(username: string, newPassword: string): UserAction {
    return {
        type: UserTypes.NEW_PASSWORD_REQUIRED_CHANGE,
        payload: {
            username,
            newPassword,
        },
    };
}

export function forgotPassword(): UserAction {
    return {
        type: UserTypes.FORGOT_PASSWORD,
    };
}

export function rememberedPassword(): UserAction {
    return {
        type: UserTypes.REMEMBERED_PASSWORD,
    };
}

export function forgotPasswordGetCode(username: string): UserAction {
    return {
        type: UserTypes.FORGOT_PASSWORD_GET_CODE,
        payload: {
            username,
        },
    };
}

interface ForgotPasswordConfirmAction {
    verificationCode: string;
    newPassword: string;
}
export function forgotPasswordConfirm(verificationCode: string, newPassword: string): UserAction {
    return {
        type: UserTypes.FORGOT_PASSWORD_CONFIRM,
        payload: {
            verificationCode,
            newPassword,
        },
    };
}

export function mfaRequired(mfa: NonNullable<AuthState['mfa']>): UserAction {
    return {
        type: UserTypes.MFA_REQUIRED,
        payload: mfa
    }
}

export function submitMFA(code: string): UserAction {
    return {
        type: UserTypes.SUBMIT_MFA,
        payload: code
    }
}

export function login(username?: string, password?: string): UserAction {
    return {
        type: UserTypes.LOGIN,
        payload: {
            username,
            password,
        },
    };
}

export function setState(state: connect.AgentStateDefinition): UserAction {
    return {
        type: UserTypes.USER_SET_STATE,
        payload: {
            state,
        },
    };
}

export function setAvailable(): UserAction {
    return {
        type: UserTypes.USER_AVAILABLE,
    };
}

export function setNotAvailable(): UserAction {
    return {
        type: UserTypes.USER_NOT_AVAILABLE,
    };
}

export function getQuickConnects(): UserAction {
    return {
        type: UserTypes.GET_QUICK_CONNECTS,
    };
}

export function toggleQuickConnects(): UserAction {
    return {
        type: UserTypes.TOGGLE_QUICK_CONNECTS,
    };
}

export function showQuickConnects(): UserAction {
    return {
        type: UserTypes.SHOW_QUICK_CONNECTS,
    };
}

export function hideQuickConnects(): UserAction {
    return {
        type: UserTypes.HIDE_QUICK_CONNECTS,
    };
}

export function afterCallWork(): UserAction {
    return {
        type: UserTypes.AFTER_CALL_WORK,
    };
}

export function afterCallWorkEnd(): UserAction {
    return {
        type: UserTypes.AFTER_CALL_WORK_END,
    };
}

export function callIssue(): UserAction {
    return {
        type: UserTypes.CALL_ISSUE,
    };
}

export function userError(): UserAction {
    return {
        type: UserTypes.USER_ERROR,
    };
}

export function refresh(): UserAction {
    return {
        type: UserTypes.REFRESH,
    };
}

export function setTimeRange(timeRange: timeRange): UserAction {
    return {
        type: UserTypes.SET_TIME_RANGE,
        payload: timeRange,
    };
}

// export function updateNotes(notes: string) : UserAction{
//     return {
//         type: UserTypes.UPDATE_CALL_NOTES,
//         payload: notes,
//     };
// }

export const userAuthenticationHasSet = (): UserAction => {
    return {
        type: UserTypes.USER_AUTHENTICATION_HAS_SET
    }
};

export function authenticateExternalUser(credentials: any) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {
        dispatch<UserAction>({
            type: UserTypes.AUTH_EXTERNAL_USER_REQUEST,
            payload: {
                credentials,
                ccpURL: getState().app.instance!.ccpURL,
            }
        })
    }
}

export function authenticateUser(token: string, tokenInBody: boolean = false) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {
        dispatch<UserAction>({
            type: UserTypes.AUTHENTICATING_USER,
        });

        const { app, user, auth } = getState();

        if (!app.instance) {
            if (app.instances?.length !== 1) {
                console.log("Unable to log user back in - multiple instances for client and instance not selected yet");
                dispatch(
                    authError(`Session expired, please login`)
                );
                return;
            }
        }

        const companyID = app.ID;
        const username = user ? user.username : undefined;
        const instanceID = app.instance?.ID || app.instances[0].ID;

        try {
            const res = await AuthAPI.authenticateUser(companyID, token, instanceID, username, tokenInBody, getParameterByName('identityManagement') || undefined)
            const refreshToken = res.headers['x-refresh-token'];
            const authToken = res.data;

            if (authToken.credentials) {
                dispatch(authenticateExternalUser(authToken.credentials));
            }

            //If no refresh token then fresh login
            if (!auth.refreshToken) {
                dispatch(setRedirect('/'))
            }

            return dispatch<UserAction>(
                userAuthenticated({
                    token: authToken.ID,
                    refreshToken: refreshToken,
                    features: authToken.features,
                    agentID: authToken.userID,
                    roles: authToken.securityRoles,
                    hierarchyStructure: authToken.hierarchyStructure
                })
            );
        } catch (ex: any) {
            console.warn(ex);
            if (ex.response && ex.response.status === 404) {
                dispatch(
                    authError(`User does not exist in connect`)
                );
            } else {
                const errorMessage = ex?.response?.data?.error || ex?.response?.data?.message || '';
                dispatch(authError(errorMessage));
                // dispatch<UserAction>({
                //     type: UserTypes.INCORRECT_TOKEN,
                // });
            }
        }
    };
}

export function fetchAgentStats() {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, UserAction>,
        getState: () => RootState
    ) => {
        const companyID = getState().app.ID;
        const token = getState().auth.token!;
        const agentID = getState().user.agentID;
        const instanceID = getState().app.instance!.ID;
        const timeRange = getState().metrics.filters.timeRange ?? getState().user.timeRange;
        const timeZone = getState().metrics.filters.timeZone;
        const from =
            timeRange === 'midnight' ?
                getMidnight(timeZone) : new Date(Date.now() - timeRange * 3600000).toISOString();
        const queueStatsFeature = getState().app.features.find(f => f.ID === AppFeatures.QUEUE_STATS) as IFeature<AppFeatures.QUEUE_STATS>
        if (queueStatsFeature?.config?.hideHistoricMetrics) return;
        try {
            const agentMetrics = agentID
                ? await ContactAPI.getQueueMetrics(companyID, instanceID, token, from, {
                      agentID,
                  }).catch(err => console.error('Error getting agentMetrics:', err))
                : [];

            return dispatch({
                type: UserTypes.FETCH_AGENT_STATS,
                payload: agentMetrics
            });
        } catch (err) {
            console.warn(err);
            const e = err as AxiosError;

            if (e.response?.status === 403) {
                return dispatch(logout());
            }
        }
    };
}

export function refreshToken() {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, UserAction>,
        getState: () => RootState
    ) => {
        dispatch<UserAction>({
            type: UserTypes.VALIDATING_TOKEN,
        });
        const companyID = getState().app.ID;
        const username = getState().user.username;
        const refreshToken = getState().auth.refreshToken;
        const instanceID = getState().app.instance!.ID || null;
        try {
            const res = await AuthAPI.refreshToken(companyID, username, refreshToken!, instanceID!);
            const XRefreshToken = res.headers['x-refresh-token'];
            const authToken = res.data;

            return dispatch(
                userAuthenticated({
                    token: authToken.ID,
                    refreshToken: XRefreshToken,
                    features: authToken.features,
                    agentID: authToken.userID,
                    roles: authToken.securityRoles,
                    hierarchyStructure: authToken.hierarchyStructure
                })
            );
        } catch (err) {
            const { authenticated } = getState().auth;
            const e = err as AxiosError;

            if (e.response?.status === 401) {
                //If not authenticated then refresh token has expired and we can request another
                if (authenticated) {
                    dispatch(logout());
                } else {
                    dispatch(requestToken());
                }
            }
            console.log('error on refresh', err)
        }
    };
}

export function requestToken() {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {
        dispatch<UserAction>({
            type: UserTypes.REQUESTING_TOKEN,
        });
        const companyID = getState().app.ID;
        const username = getState().user.username;
        const instanceID = getState().app.instance!.ID;
        try {
            await AuthAPI.requestToken(companyID, username, instanceID);

            dispatch<UserAction>({
                type: UserTypes.TOKEN_REQUESTED,
            });
        } catch (ex) {
            dispatch<UserAction>({
                type: UserTypes.TOKEN_ERROR,
            });
        }
    };
}

export function tokenError() {
    return {
        type: UserTypes.TOKEN_ERROR,
    };
}

export function userAuthenticated(token: UserAuthenticatedAction): UserAction {
    console.log('authenticated');
    return {
        type: UserTypes.USER_AUTHENTICATED,
        payload: token,
    };
}

export function setAfterCallStatus(status: connect.AgentStateDefinition): UserAction {
    return {
        type: UserTypes.SET_AFTER_CALL_STATUS,
        payload: status
    }
}

export function setLogoutCountdown() {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, any>,
        getState: () => RootState
    ) => {

        const companyID = getState().app.ID;
        const agentID = getState().user.agentID;
        const instanceID = getState().app.instance!.ID;
        const token = getState().auth.token!;
        try {
            const agent = await AgentAPI.getSingleUser(companyID, instanceID, token, agentID);

            const loggedInDate = new Date(agent.lastLogin).getTime();
            const loggedOutTime = new Date(loggedInDate).setTime(loggedInDate + (10 * MS_IN_HOUR));

            setInterval(() => {
                const now = Date.now();

                const diff = Math.floor(loggedOutTime - now);
                const min = Math.floor(diff / 60000);

                dispatch(setLogoutTime(min));

            }, 60000);

            const { timeZone, tags = {} } = agent;

            dispatch({
                type: UserTypes.USER_LOADED,
                payload: { timeZone, tags },
            })

        } catch (ex) {
            dispatch<UserAction>({
                type: UserTypes.USER_ERROR,
            });
        }
    };
}

export function setLogoutTime(time: number) {
    return {
        type: UserTypes.SET_LOGOUT_COUNTDOWN,
        payload: time,
    };
}

export function setShowNextStatus(shouldShow: boolean): UserAction {
    return {
        type: UserTypes.SET_SHOW_NEXT_STATUS,
        payload: shouldShow,
    }
}

export function updateStatusControl(options: StatusControl): UserAction {
    return {
        type: UserTypes.SET_STATUS_CONTROL,
        payload: options
    }
}
